
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Star } from "lucide-react";
import { Link } from "react-router-dom";

interface AgentCardProps {
  agent: {
    id: string;
    name: string;
    description: string;
    category: string;
    imageUrl?: string;
    author: string;
    downloads: number;
    rating: number;
    isPremium?: boolean;
    price?: string | number;
    creator?: {
      name: string;
      avatar?: string;
    };
  };
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const { id, name, description, imageUrl, downloads, rating, isPremium } = agent;
  
  // Use creator.name if available, otherwise fallback to author
  const creatorName = agent.creator?.name || agent.author;
  const creatorAvatar = agent.creator?.avatar;
  
  // Determine price display
  const price = agent.price === 0 || agent.price === "Free" || !isPremium ? "Free" : agent.price;

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <div className="relative h-48 bg-gray-100">
        <img
          src={imageUrl || `/placeholder.svg`}
          alt={name}
          className="object-cover w-full h-full"
        />
      </div>
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-lg line-clamp-1">{name}</h3>
          <Badge variant={isPremium ? "default" : "outline"}>
            {isPremium ? "Premium" : "Free"}
          </Badge>
        </div>
        <p className="text-gray-500 text-sm mb-3 line-clamp-2">{description}</p>
        <div className="flex items-center text-sm text-gray-500">
          <div className="flex items-center mr-4">
            <Star className="h-4 w-4 text-yellow-400 mr-1" />
            <span>{rating || "N/A"}</span>
          </div>
          <div className="flex items-center">
            <Download className="h-4 w-4 mr-1" />
            <span>{downloads}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="p-5 pt-0 flex justify-between items-center">
        <div className="flex items-center">
          <div className="w-6 h-6 rounded-full bg-gray-200 overflow-hidden relative mr-2">
            <img
              src={creatorAvatar || `/placeholder.svg`}
              alt={creatorName}
              className="object-cover w-full h-full"
            />
          </div>
          <span className="text-sm text-gray-600">{creatorName}</span>
        </div>
        <Link to={`/agents/${id}`}>
          <Button size="sm" variant="outline">
            View
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

export default AgentCard;
